import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { teamsAzureAdScopes, nexusTeamsAzureAdScopes } from './msalConfig';
import { acquireTeamsTokenAsync } from './TokenAcquisitionService';
import { trackException } from '../../app/telemetry/appInsights';

// NAA (Nested App Authentication) replaces the legacy Teams auth flow.
// The Teams broker populates the MSAL token cache before the app renders,
// so acquireTokenSilent succeeds on first call. When interaction is required
// (first consent, MFA step-up, etc.), acquireTokenPopup opens the broker-managed

export interface EnsureTeamsAuthenticatedSessionArgs {
    msalInstance: IPublicClientApplication;
}

// For NAA, the host supplies the account context for token requests.
// We only use the cached account to initialize app state that still expects AccountInfo.
export const getTeamsCachedAccount = (msalInstance: IPublicClientApplication): AccountInfo | null => {
    return msalInstance.getAllAccounts()[0] ?? null;
};

/**
 * Checks whether MSAL already has an account in cache (populated by the NAA
 * broker before the app loaded). If so, returns true.
 * Returns false if there is no cached account — the caller should then trigger
 * loginTeamsAsync to initiate interactive consent via the broker.
 */
export const ensureTeamsAuthenticatedSessionAsync = async ({
    msalInstance,
}: EnsureTeamsAuthenticatedSessionArgs): Promise<boolean> => {
    return Boolean(getTeamsCachedAccount(msalInstance));
};

export interface LoginTeamsAsyncArgs {
    msalInstance: IPublicClientApplication;
}

/**
 * Triggers an interactive token acquisition via the Teams broker (NAA).
 * The broker shows the consent/sign-in dialog; no custom popup pages are needed.
 * On success, the broker should populate the MSAL cache with the current account.
 */
export const loginTeamsAsync = async ({ msalInstance }: LoginTeamsAsyncArgs): Promise<AccountInfo | null> => {
    try {
        const result = await msalInstance.acquireTokenPopup({ scopes: teamsAzureAdScopes });
        const resolvedAccount = result.account ?? getTeamsCachedAccount(msalInstance);
        if (resolvedAccount) {
            return resolvedAccount;
        }
    } catch (error) {
        trackException(error, {
            source: 'loginTeamsAsync',
            environment: 'Teams',
            numScopes: teamsAzureAdScopes.length.toString(),
        });
        // Broker interaction failed or was cancelled.
    }

    return null;
};


export interface AcquireTeamsAccessTokenArgs {
    msalInstance: IPublicClientApplication;
}

/**
 * Acquires an API access token in the Teams (NAA) context.
 * acquireTeamsTokenAsync handles the silent → broker popup fallback.
 * For NAA, the host supplies account context for the token request.
 */
export const acquireTeamsAccessTokenAsync = async ({
    msalInstance,
}: AcquireTeamsAccessTokenArgs): Promise<string> => {
    return await acquireTeamsTokenAsync(msalInstance, teamsAzureAdScopes);
};

export const acquireNexusTeamsAccessTokenAsync = async ({
    msalInstance,
}: AcquireTeamsAccessTokenArgs): Promise<string> => {
    return await acquireTeamsTokenAsync(msalInstance, nexusTeamsAzureAdScopes);
};
